import { generatePath } from '@toggle/helpers';
import { EarningsInsight, EarningsItemBase, Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { EarningsInsightPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';

import * as S from './EarningsInsightSection.styles';
import { PreviewContent } from './PreviewContent';
import { RecapContent } from './RecapContent';
import { getRecapTitle } from './utils';

export interface EarningsInsightSectionProps {
  entity: Entity;
  earningsData?: EarningsItemBase;
  earningsInsight?: EarningsInsight;
  isLoading: boolean;
  isPreview: boolean;
}

export const EarningsInsightSection = ({
  entity,
  earningsData,
  earningsInsight,
  isLoading,
  isPreview,
}: EarningsInsightSectionProps) => {
  const { t } = useTranslation(['widget']);
  const navigate = useNavigate();

  if (!earningsData) {
    return null;
  }

  const handleExpandClick = () => {
    const requestPath = generatePath({
      basePath: appPaths.assetOverview,
      params: {
        [queryKeys.assetOverviewAsset]: entity.tag,
        [queryKeys.assetOverviewTab]: 'earnings',
      },
    });
    navigate(requestPath);
  };

  return (
    <S.Root>
      <S.Header>
        <S.TitleContainer>
          <S.Title>
            {isPreview
              ? t('widget:assetOverview.earningsSection.upcomingEarnings')
              : getRecapTitle(
                  t,
                  earningsData.fiscal_period,
                  earningsData.fiscal_year
                )}
          </S.Title>
          {earningsInsight && (
            <EarningsInsightPill
              isPreview={isPreview}
              entityTag={entity.tag}
              earningsInsight={earningsInsight}
              source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
            />
          )}
        </S.TitleContainer>
        <S.ExpandButton onClick={handleExpandClick}>
          <S.ExpandIcon iconName="Expand" size={16} />
        </S.ExpandButton>
      </S.Header>
      {isLoading ? (
        <S.StyledSkeletonLoader />
      ) : (
        <S.Content>
          {isPreview ? (
            <PreviewContent earningsData={earningsData} />
          ) : (
            <RecapContent earningsData={earningsData} entity={entity} />
          )}
        </S.Content>
      )}
    </S.Root>
  );
};
